import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { curatorCardApi, type CuratorCardEnrollment, type CuratorCardStudent } from '@/api/curatorCard';
import { usersApi } from '@/api/users';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Pill } from '@/components/ui/Pill';
import { SearchInput } from '@/components/ui/SearchInput';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MStats, MTag } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { avgResponse, curatorRowActions } from '@/lib/curatorCardActions';
import { filterCuratorStudents } from '@/lib/curatorCardView';
import { lifecycleErrorText } from '@/lib/enrollmentActions';
import { AssignStudentModal } from './AssignStudentModal';
import { CuratorReviewsFeed } from './CuratorReviewsFeed';

/**
 * Карточка куратора в админке (решение владельца 28.09.2026 «админке и кураторам — удобно»): сводка, его ученики и
 * лента проверок. Договор — `docs/CONTRACTS/curator-card.md`.
 *
 * ⚠️ УЧЕНИКИ — ПО ПРАВИЛУ ОЧЕРЕДИ ДОМАШЕК КУРАТОРА (активные записи, личные и через его группы). Список учеников
 * users (`/admin/users?curator_id=`) берёт и снятые записи — для карточки он не годится, см. техдолг.
 */
export function CuratorCard({ curatorId }: { curatorId: string }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const [assignOpen, setAssignOpen] = useState(false);
  const [studentsQ, setStudentsQ] = useState('');

  const overview = useQuery({
    queryKey: ['admin', 'curator', curatorId, 'overview'],
    queryFn: () => curatorCardApi.overview(curatorId),
  });
  const data = overview.data;
  const avg = avgResponse(data?.summary.avg_response_hours_30d);
  // Поиск по имени, почте, курсу и группе; порядок — серверный: у кого больше работ на проверке — выше (28.09.2026).
  const students = filterCuratorStudents(data?.students ?? [], studentsQ);

  return (
    <>
      <Card className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle>{t('adminCuratorCard.summaryTitle')}</CardTitle>
          {isAdmin && (
            // На телефоне — во всю ширину под заголовком, 44 px (§5.7).
            <Button size="sm" onClick={() => setAssignOpen(true)} className="max-md:min-h-11 max-md:w-full">
              <span className="material-symbols-outlined">person_add</span>
              {t('adminCuratorCard.assign')}
            </Button>
          )}
        </div>
        {overview.isLoading ? (
          <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
        ) : overview.error || !data ? (
          <p className="mt-3 text-sm text-error">{t('adminCuratorCard.loadError')}</p>
        ) : isMobile ? (
          // Телефон: три числа одной строкой (`MStats`), пояснения — строкой под ними, а не три плитки столбиком.
          <div className="mt-3 space-y-2" data-curator-summary-phone>
            <MStats
              cols={3}
              items={[
                { value: data.summary.students, label: t('adminCuratorCard.students') },
                {
                  value: data.summary.pending_submissions,
                  label: t('adminCuratorCard.pending'),
                  tone: data.summary.pending_submissions > 0 ? 'warm' : 'plain',
                },
                { value: avg ? t(avg.key, { n: avg.n }) : '—', label: t('adminCuratorCard.avgResponse') },
              ]}
            />
            <p className="px-1 text-[11.5px] text-on-surface-variant">
              {t('adminCuratorCard.pendingStudents', { count: data.summary.pending_students })} ·{' '}
              {t('adminCuratorCard.reviewed30d', { count: data.summary.reviewed_30d })}
            </p>
          </div>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Tile icon="groups" label={t('adminCuratorCard.students')} value={String(data.summary.students)} />
            <Tile
              icon="hourglass_top"
              label={t('adminCuratorCard.pending')}
              value={String(data.summary.pending_submissions)}
              hint={t('adminCuratorCard.pendingStudents', { count: data.summary.pending_students })}
            />
            <Tile
              icon="timer"
              label={t('adminCuratorCard.avgResponse')}
              value={avg ? t(avg.key, { n: avg.n }) : '—'}
              hint={t('adminCuratorCard.reviewed30d', { count: data.summary.reviewed_30d })}
            />
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <CardTitle>{t('adminCuratorCard.studentsTitle')}</CardTitle>
        {data && data.students.length === 0 ? (
          <p className="mt-3 text-sm text-on-surface-variant">{t('adminCuratorCard.noStudents')}</p>
        ) : data ? (
          <>
          <SearchInput
            className="mt-3"
            value={studentsQ}
            placeholder={t('adminCuratorCard.studentsSearch')}
            onSearch={setStudentsQ}
          />
          <p className="mt-2 text-xs text-on-surface-variant">
            {t('common.shownOf', { shown: students.length, total: data.students.length })}
          </p>
          {isMobile ? (
            // Телефон (§5.7 п. 4): таблица в 760 px уезжала вбок — строками: ученик, когда заходил, «ждёт проверки»,
            // под ним записи на курсы с «⋯». Без внутренней прокрутки: вложенная прокрутка на телефоне ловит палец.
            <div className="mt-2" data-curator-students-phone>
              {students.map((s, i) => (
                <StudentPhoneRow key={s.student_id} curatorId={curatorId} student={s} last={i === students.length - 1} />
              ))}
              {students.length === 0 && (
                <p className="mt-3 text-sm text-on-surface-variant">{t('common.nothingFound')}</p>
              )}
            </div>
          ) : (
          /* Прокрутка на ~10 строк: у куратора до сотни учеников, лента проверок не должна уезжать за экраны. */
          <div className="mt-2 max-h-[36rem] overflow-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-xs uppercase tracking-label text-on-surface-variant">
                <tr>
                  <th className="py-2 pr-3">{t('adminCuratorCard.colStudent')}</th>
                  <th className="py-2 pr-3">{t('adminCuratorCard.colCourse')}</th>
                  <th className="py-2 pr-3">{t('adminCuratorCard.colLastSeen')}</th>
                  <th className="py-2 pr-3">{t('adminCuratorCard.colPending')}</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <StudentRow key={s.student_id} curatorId={curatorId} student={s} />
                ))}
              </tbody>
            </table>
            {students.length === 0 && (
              <p className="mt-3 text-sm text-on-surface-variant">{t('common.nothingFound')}</p>
            )}
          </div>
          )}
          </>
        ) : null}
      </Card>

      <CuratorReviewsFeed curatorId={curatorId} />

      {assignOpen && (
        <AssignStudentModal open={assignOpen} onClose={() => setAssignOpen(false)} curatorId={curatorId} />
      )}
    </>
  );
}

function Tile({ icon, label, value, hint }: { icon: string; label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-card bg-surface-container-highest p-3">
      <div className="flex items-center gap-1.5 text-on-surface-variant">
        <span className="material-symbols-outlined text-[18px]">{icon}</span>
        <span className="text-xs">{label}</span>
      </div>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-on-surface-variant">{hint}</p>}
    </div>
  );
}

function StudentRow({ curatorId, student }: { curatorId: string; student: CuratorCardStudent }) {
  const { t } = useTranslation();
  return (
    <tr className="border-t border-outline-variant/40 align-top">
      <td className="py-2 pr-3">
        <Link to={`/admin/students/${student.student_id}`} className="flex items-center gap-2 hover:underline">
          <Avatar name={student.full_name} src={student.avatar_url || undefined} size="sm" />
          <span className="font-medium">{student.full_name || student.student_id.slice(0, 8)}</span>
        </Link>
      </td>
      <td className="py-2 pr-3">
        <div className="space-y-2">
          {student.enrollments.map((e) => (
            <EnrollmentLine key={e.enrollment_id} curatorId={curatorId} enrollment={e} />
          ))}
        </div>
      </td>
      <td className="py-2 pr-3 text-on-surface-variant">
        {student.last_seen_at ? new Date(student.last_seen_at).toLocaleString('ru-RU') : '—'}
      </td>
      <td className="py-2 pr-3">
        {student.pending > 0 ? (
          <Pill variant="warning">{t('adminCuratorCard.pendingYes', { count: student.pending })}</Pill>
        ) : (
          <span className="text-on-surface-variant">{t('adminCuratorCard.pendingNo')}</span>
        )}
      </td>
    </tr>
  );
}

/** Ученик куратора на телефоне: строка вместо строки таблицы, записи на курсы — под ней, у каждой своё «⋯». */
function StudentPhoneRow({ curatorId, student, last }: { curatorId: string; student: CuratorCardStudent; last: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={cn('py-3', !last && 'border-b border-outline')} data-curator-student-row>
      <div className="flex items-center gap-3">
        <Link to={`/admin/students/${student.student_id}`} className="flex min-w-0 flex-1 items-center gap-3">
          <Avatar name={student.full_name} src={student.avatar_url || undefined} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold text-on-surface">
              {student.full_name || student.student_id.slice(0, 8)}
            </span>
            <span className="block truncate text-[11.5px] text-on-surface-variant">
              {/* Короткая подпись: «Последняя активность: дата» в 390 обрезалась до «Последняя активность: …». */}
              {t('adminDetail.lastSeen')}:{' '}
              {student.last_seen_at ? new Date(student.last_seen_at).toLocaleString('ru-RU') : '—'}
            </span>
          </span>
        </Link>
        {/* «Да, работ: N» — ответ на заголовок столбца таблицы; без таблицы на телефоне — «N на проверке». */}
        {student.pending > 0 && <MTag tone="warm">{t('staffMobile.pendingWorks', { count: student.pending })}</MTag>}
      </div>
      <div className="mt-1.5 space-y-1.5 pl-[52px]">
        {student.enrollments.map((e) => (
          <EnrollmentLine key={e.enrollment_id} curatorId={curatorId} enrollment={e} phone />
        ))}
      </div>
    </div>
  );
}

function EnrollmentLine({
  curatorId,
  enrollment: e,
  phone = false,
}: {
  curatorId: string;
  enrollment: CuratorCardEnrollment;
  /** Телефон: действия записи — листом «⋯» (правило 4), а не рядом кнопок под курсом. */
  phone?: boolean;
}) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const actions = curatorRowActions(e, role);
  const [dialog, setDialog] = useState<'moveGroup' | 'changeCurator' | null>(null);
  const [sheet, setSheet] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'curator'] });
    qc.invalidateQueries({ queryKey: ['admin', 'curators'] });
    qc.invalidateQueries({ queryKey: ['admin', 'student'] });
    qc.invalidateQueries({ queryKey: ['admin', 'marathon', e.marathon_id, 'groups'] });
  };

  const removeFromGroup = useMutation({
    mutationFn: () => usersApi.removeGroupMembers(e.group_id!, [e.enrollment_id]),
    onSuccess: refresh,
  });
  const askRemoveFromGroup = async () => {
    if (
      await ask({
        message: t('adminCuratorCard.confirmRemoveFromGroup', { group: e.group_name ?? '', course: e.marathon_title }),
        danger: true,
      })
    ) {
      removeFromGroup.mutate();
    }
  };
  const title = e.marathon_title || e.marathon_id.slice(0, 8);
  const via = e.group_id
    ? t('adminCuratorCard.viaGroup', { name: e.group_name ?? e.group_id.slice(0, 8) })
    : t('adminCuratorCard.viaPersonal');

  return (
    <div>
      {phone ? (
        <div className="flex items-start gap-1" data-curator-enrollment-phone>
          <div className="min-w-0 flex-1 pt-1">
            <p className="text-[13px] leading-snug text-on-surface">{title}</p>
            <p className="text-[11.5px] text-on-surface-variant">{via}</p>
            {actions.includes('changeCurator') && (
              <p className="text-[11px] text-on-surface-variant">{t('adminCuratorCard.personalHint')}</p>
            )}
          </div>
          {actions.length > 0 && (
            <button
              type="button"
              onClick={() => setSheet(true)}
              aria-label={t('nav.more')}
              className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
            >
              <span className="material-symbols-outlined text-[20px]">more_horiz</span>
            </button>
          )}
          <ActionSheet
            open={sheet}
            onClose={() => setSheet(false)}
            title={title}
            actions={[
              ...(actions.includes('moveGroup')
                ? [{ label: t('adminCuratorCard.moveGroup'), icon: 'sync_alt', onSelect: () => setDialog('moveGroup') }]
                : []),
              ...(actions.includes('removeFromGroup')
                ? [{ label: t('adminCuratorCard.removeFromGroup'), icon: 'person_remove', danger: true, onSelect: askRemoveFromGroup }]
                : []),
              ...(actions.includes('changeCurator')
                ? [{ label: t('adminCuratorCard.changeCurator'), icon: 'person', onSelect: () => setDialog('changeCurator') }]
                : []),
            ]}
          />
        </div>
      ) : (
      <>
      <p>{title}</p>
      <p className="text-xs text-on-surface-variant">{via}</p>
      </>
      )}
      {!phone && actions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {actions.includes('moveGroup') && (
            <Button size="sm" variant="ghost" onClick={() => setDialog('moveGroup')}>
              {t('adminCuratorCard.moveGroup')}
            </Button>
          )}
          {actions.includes('removeFromGroup') && (
            <Button size="sm" variant="ghost" loading={removeFromGroup.isPending} onClick={askRemoveFromGroup}>
              {t('adminCuratorCard.removeFromGroup')}
            </Button>
          )}
          {actions.includes('changeCurator') && (
            <>
              <Button size="sm" variant="ghost" onClick={() => setDialog('changeCurator')}>
                {t('adminCuratorCard.changeCurator')}
              </Button>
              <span className="self-center text-xs text-on-surface-variant">{t('adminCuratorCard.personalHint')}</span>
            </>
          )}
        </div>
      )}
      {removeFromGroup.error && (
        <p className="mt-1 text-xs text-error">{lifecycleErrorText(removeFromGroup.error, t)}</p>
      )}
      {dialog && (
        <ChangeDialog
          mode={dialog}
          curatorId={curatorId}
          enrollment={e}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/** «Сменить группу» у записи в группе и «Сменить куратора» у личной записи. */
function ChangeDialog({
  mode,
  curatorId,
  enrollment: e,
  onClose,
  onDone,
}: {
  mode: 'moveGroup' | 'changeCurator';
  curatorId: string;
  enrollment: CuratorCardEnrollment;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [pick, setPick] = useState('');

  const groups = useQuery({
    queryKey: ['admin', 'marathon', e.marathon_id, 'groups'],
    queryFn: () => usersApi.listGroups(e.marathon_id),
    enabled: mode === 'moveGroup',
  });
  const curators = useQuery({
    queryKey: ['admin', 'curators'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 100 }),
    enabled: mode === 'changeCurator',
  });

  const apply = useMutation({
    mutationFn: () =>
      mode === 'moveGroup'
        ? usersApi.moveToGroup(e.enrollment_id, e.group_id, pick)
        : usersApi.assignCurator(e.enrollment_id, pick),
    onSuccess: onDone,
  });

  const options =
    mode === 'moveGroup'
      ? (groups.data?.items ?? [])
          .filter((g) => g.id !== e.group_id)
          .map((g) => ({
            id: g.id,
            label: `${g.name} · ${g.curators.map((c) => c.name).join(', ') || t('adminDetail.groupNoCurators')}`,
          }))
      : (curators.data?.items ?? [])
          .filter((c) => c.id !== curatorId && c.status === 'active')
          .map((c) => ({ id: c.id, label: c.full_name }));

  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полем, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!pick}>
        {t('adminCuratorCard.apply')}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'moveGroup' ? t('adminCuratorCard.moveGroup') : t('adminCuratorCard.changeCurator')}
      size="sm"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3">
        <p className="text-sm text-on-surface-variant">{e.marathon_title}</p>
        <select
          value={pick}
          onChange={(ev) => setPick(ev.target.value)}
          className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
        >
          <option value="">{mode === 'moveGroup' ? t('adminDetail.groupPick') : t('adminCuratorCard.curatorPick')}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        {mode === 'moveGroup' && groups.data && options.length === 0 && (
          <p className="text-xs text-warning">{t('adminCuratorCard.noOtherGroups')}</p>
        )}
        {apply.error && <p className="text-sm text-error">{lifecycleErrorText(apply.error, t)}</p>}
        {!isMobile && buttons}
      </div>
    </Modal>
  );
}
